import { memo, useCallback } from 'react';

import { Popover } from '@air/primitive-popover';

import { CreditUsageMeterRing } from '~/components/CreditUsageMeter/CreditUsageMeterRing';
import { useCreditMeterViewTracking } from '~/components/CreditUsageMeter/useCreditMeterViewTracking';
import { useCreditUsagePercent } from '~/components/CreditUsageMeter/useCreditUsagePercent';
import { WorkspaceCreditsMeterPopover } from '~/components/CreditUsageMeter/WorkspaceCreditsMeterPopover';
import { MainSideNavItemButton } from '~/components/v2/components/workspace/main-side-nav/item/MainSideNavItemButton';
import { MainSideNavItemIcon } from '~/components/v2/components/workspace/main-side-nav/item/MainSideNavItemIcon';
import { MainSideNavItemLabel } from '~/components/v2/components/workspace/main-side-nav/item/MainSideNavItemLabel';
import { useMainSideNavOverlay } from '~/components/v2/components/workspace/main-side-nav/useMainSideNavOverlay';
import { useCreditStats } from '~/swr-hooks/workspaces/useCreditStats';

/** 10px past the rail edge, matching the notifications tray. */
const CREDIT_USAGE_POPOVER_SIDE_OFFSET_PX = 10;

export const MainSideNavItemCredits = memo(() => {
  const { data: creditStats } = useCreditStats();
  const { label, percentUsed } = useCreditUsagePercent();
  const { isOpen, onOpenChange: onOverlayOpenChange } = useMainSideNavOverlay({ id: 'credits' });
  const { onMeterOpenChange } = useCreditMeterViewTracking({ location: 'side_nav' });

  const handleOpenChange = useCallback(
    (open: boolean) => {
      onOverlayOpenChange(open);
      onMeterOpenChange(open);
    },
    [onMeterOpenChange, onOverlayOpenChange],
  );

  if (!creditStats?.allocated) {
    return null;
  }

  return (
    // 320px fits the count + "Upgrade plan" / "Add credits" pair.
    <Popover
      align="end"
      aria-label="Workspace credit usage"
      className="motion-safe:radix-side-right:fade-in motion-safe:radix-side-right:slide-in-from-left-12 motion-safe:radix-side-right:animation-duration-300 w-80 rounded-8 p-0 motion-safe:radix-side-right:animate-in"
      onOpenChange={handleOpenChange}
      open={isOpen}
      side="right"
      sideOffset={CREDIT_USAGE_POPOVER_SIDE_OFFSET_PX}
      trigger={
        <MainSideNavItemButton data-item-id="credits" data-main-side-nav-overlay type="button">
          <MainSideNavItemIcon>
            <CreditUsageMeterRing percentUsed={percentUsed} />
          </MainSideNavItemIcon>
          <MainSideNavItemLabel>{label}</MainSideNavItemLabel>
        </MainSideNavItemButton>
      }
    >
      <WorkspaceCreditsMeterPopover creditPackModalSource="side_nav" />
    </Popover>
  );
});

MainSideNavItemCredits.displayName = 'MainSideNavItemCredits';
